import { SortAscendingOutlined } from '@ant-design/icons';
import { Flex, Select } from 'antd';

import {
  SPACE_MD,
  SPACE_MD_SM,
  SPACE_SM,
  SPACE_XS,
} from '@/theme/buildAntdTheme';

import { renderListingToolbarActionItem } from './listingActionRender';

import type {
  ListingSortToolConfig,
  ListingToolbarActionItem,
} from './listingToolbarConfig';

export type FilteredListingToolbarProps = {
  /** Omit or pass null to hide the sort control (e.g. grouped list view). */
  sort?: ListingSortToolConfig | null;
  /** Declarative toolbar actions (preferred). */
  actionItems?: ListingToolbarActionItem[];
  emphasis?: 'default' | 'prominent';
};

export function FilteredListingToolbar({
  sort,
  actionItems,
  emphasis = 'default',
}: FilteredListingToolbarProps) {
  const leadingItems =
    actionItems?.filter((item) => item.placement === 'start') ?? [];
  const trailingItems =
    actionItems?.filter((item) => item.placement !== 'start') ?? [];
  const hasActions = leadingItems.length > 0 || trailingItems.length > 0;
  if (!sort && !hasActions) return null;

  return (
    <Flex
      wrap
      gap="small"
      align="center"
      justify="space-between"
      data-testid="ui-filtered-listing-toolbar"
      style={
        emphasis === 'prominent'
          ? {
              padding: `${SPACE_XS + SPACE_SM}px ${SPACE_MD_SM}px`,
              background: 'var(--ant-color-fill-quaternary)',
              border: '1px solid var(--ant-color-border-secondary)',
              borderRadius: 'var(--ant-border-radius)',
            }
          : undefined
      }
    >
      <Flex
        wrap
        flex="1 1 360px"
        gap="small"
        align="center"
        style={{ minWidth: 0 }}
      >
        {sort ? (
          <Flex wrap gap="small" align="center">
            <Select
              value={sort.value}
              onChange={(v) => sort.onChange(v)}
              aria-label={sort.options[0]?.label ?? 'Sort'}
              data-testid={sort.dataTestId}
              prefix={<SortAscendingOutlined />}
              classNames={
                sort.popupClassName
                  ? { popup: { root: sort.popupClassName } }
                  : undefined
              }
              options={sort.options}
            />
          </Flex>
        ) : null}
        {leadingItems.map((item) => renderListingToolbarActionItem(item))}
      </Flex>
      <Flex
        wrap
        gap="small"
        align="center"
        style={{ marginInlineStart: 'auto', paddingInlineStart: SPACE_MD }}
      >
        {trailingItems.length > 0 ? (
          <Flex wrap gap="small" align="center" justify="flex-end">
            {trailingItems.map((item) => renderListingToolbarActionItem(item))}
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
}
